import { useState } from "react";
import DetailsBody from "./DetailsBody";
import ColorVector from "./ColorVector";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { getSourceColor, getSourceLabel } from "../utils/cvss";

// Vinnie's own SSVC (LLM/gemma-4-31b-it) gets its color and label from
// utils/cvss — there is no SSVC composite.
const SSVC_COLORS = {
  CISA: "#e040fb",
};

const SSVC_LABELS = {
  CISA: "CISA (Official)",
};

function ssvcColor(src) {
  return SSVC_COLORS[src] || getSourceColor(src);
}

function ssvcLabel(src) {
  return SSVC_LABELS[src] || getSourceLabel(src);
}

function SsvcRadarChart({ ssvcData }) {
  const [hiddenSources, setHiddenSources] = useState(new Set());
  const [detailsSource, setDetailsSource] = useState(null);

  if (!ssvcData) return null;

  const sources = Object.keys(ssvcData);
  if (sources.length === 0) return null;

  const shownSources = sources.filter((s) => !hiddenSources.has(s));

  const toggleSource = (src) => {
    setHiddenSources((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      return next;
    });
  };

  // Collect all unique metrics across sources, separating Decision (output) from inputs
  const allMetrics = [];
  const decisions = {}; // src -> { label, short }
  const seen = new Set();
  for (const src of sources) {
    for (const comp of ssvcData[src].components || []) {
      if (comp.metric === "P" || comp.metric_label === "Decision") {
        decisions[src] = { label: comp.label, short: comp.short };
        continue;
      }
      if (!seen.has(comp.metric_label)) {
        seen.add(comp.metric_label);
        allMetrics.push({ key: comp.metric, label: comp.metric_label, max: comp.max });
      }
    }
  }

  if (allMetrics.length === 0) return null;

  // Build radar data: normalize each metric to 0-100%
  const data = allMetrics.map(({ key, label, max }) => {
    const entry = { metric: label, fullWidth: 100 };
    for (const src of sources) {
      const comp = (ssvcData[src].components || []).find(
        (c) => c.metric === key || c.metric_label === label
      );
      if (comp) {
        entry[src] = Math.round((comp.numeric / (comp.max || max || 1)) * 100);
        entry[`${src}_label`] = `${comp.label} (${comp.short})`;
      } else {
        entry[src] = 0;
      }
    }
    return entry;
  });

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const metric = payload[0]?.payload?.metric;
    return (
      <div
        style={{
          background: "#21262d",
          border: "1px solid #30363d",
          borderRadius: 6,
          padding: "8px 12px",
          fontSize: 12,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{metric}</div>
        {payload.map((p) => (
          <div key={p.dataKey} style={{ color: p.stroke }}>
            {ssvcLabel(p.dataKey)}:{" "}
            {p.payload[`${p.dataKey}_label`] || `${p.value}%`}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="radar-panel">
      <div className="radar-chart-container">
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={data} cx="50%" cy="50%" outerRadius="75%">
            <PolarGrid stroke="#30363d" />
            <PolarAngleAxis
              dataKey="metric"
              tick={{ fill: "#8b949e", fontSize: 12 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
              axisLine={false}
            />
            {shownSources.map((src) => (
              <Radar
                key={src}
                name={ssvcLabel(src)}
                dataKey={src}
                stroke={ssvcColor(src)}
                fill={ssvcColor(src)}
                fillOpacity={0.1}
                strokeWidth={2}
              />
            ))}
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="radar-legend">
        {sources.map((src) => {
          const visible = !hiddenSources.has(src);
          const hasDetails = !!ssvcData[src].details;
          return (
            <div key={src} className="radar-legend-item radar-toggle">
              <input
                type="checkbox"
                checked={visible}
                onChange={() => toggleSource(src)}
                className="radar-checkbox"
              />
              <span
                className="radar-legend-dot"
                style={{ background: visible ? ssvcColor(src) : "#6e7681" }}
              />
              <span className="radar-legend-source" style={{ opacity: visible ? 1 : 0.5 }}>
                {ssvcLabel(src)}
              </span>
            </div>
          );
        })}
        {Object.keys(decisions).length > 0 && (
          <div style={{ marginTop: 12, padding: "8px 0", borderTop: "1px solid #30363d" }}>
            <div style={{ fontSize: 11, color: "#6e7681", textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 4 }}>
              Decision (result)
            </div>
            {sources.map((src) => {
              const d = decisions[src];
              if (!d) return null;
              const decColor = d.short === "Act" || d.short === "A" ? "var(--severity-critical)"
                : d.short === "Attend" ? "var(--severity-high)"
                : d.short === "T*" || d.short === "Track*" ? "var(--severity-medium)"
                : "var(--severity-low)";
              return (
                <div key={src} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 13 }}>
                  <span style={{ color: ssvcColor(src), fontWeight: 600, minWidth: 100 }}>{ssvcLabel(src)}</span>
                  <span style={{ color: decColor, fontWeight: 700 }}>{d.label}</span>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 12, color: "#6e7681" }}>
          Axes show input factors. Higher = more urgent.
        </div>
      </div>

      {detailsSource && ssvcData[detailsSource]?.details && (
        <div className="details-inline">
          <div className="details-inline-header">
            <span
              className="details-inline-dot"
              style={{ background: ssvcColor(detailsSource) }}
            />
            <span style={{ fontWeight: 700 }}>
              {ssvcLabel(detailsSource)}
            </span>
            <span style={{ color: "#8b949e" }}>Justification</span>
            <button className="details-inline-close" onClick={() => setDetailsSource(null)}>
              &times;
            </button>
          </div>
          <DetailsBody text={ssvcData[detailsSource].details} />
        </div>
      )}
    </div>
  );
}

export default SsvcRadarChart;
